// Signs a staff login out after 30 minutes with no one at the screen (the owner's rule,
// 2026-10-13: a console left signed in must not be used by someone else). Activity is a
// mouse move, key press, click, scroll or touch, kept in localStorage so every open tab
// shares it: working in one tab keeps the others signed in, and all of them sign out
// together. A minute before, a notice offers "Stay signed in". Background refreshes do not
// count. A browser reopened after 30 minutes finds the session already ended (lib/idle.js).
import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { IDLE_LIMIT_MS, markActive, lastActive, markIdleSignOut } from "@/lib/idle";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Btn } from "@/components/common/Panel";

const WARN_MS = 60_000;
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

export function IdleLogout() {
  const { session, logout } = useApp();
  const [left, setLeft] = useState(null); // ms until sign-out while the notice shows

  useEffect(() => {
    if (!session) return undefined;
    markActive(true);
    let last = 0;
    const onActivity = () => { const now = Date.now(); if (now - last > 5000) { last = now; markActive(); } };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const tick = () => {
      const idle = Date.now() - lastActive();
      if (idle >= IDLE_LIMIT_MS) { markIdleSignOut(); setLeft(null); logout(); return; }
      setLeft(idle >= IDLE_LIMIT_MS - WARN_MS ? IDLE_LIMIT_MS - idle : null);
    };
    const timer = setInterval(tick, 1000);
    tick();
    return () => { clearInterval(timer); EVENTS.forEach((e) => window.removeEventListener(e, onActivity)); };
  }, [session, logout]);

  if (!session || left == null) return null;
  const secs = Math.max(0, Math.ceil(left / 1000));
  return (
    <Dialog open onOpenChange={(o) => { if (!o) markActive(true); }}>
      <DialogContent className="max-w-sm" data-testid="idle-warning">
        <DialogHeader>
          <DialogTitle className="font-display">Still there?</DialogTitle>
          <DialogDescription>No activity for 29 minutes. You will be signed out in <b className="tabular-nums" data-testid="idle-countdown">{secs}s</b> so no one else can use your login.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Btn onClick={() => { markIdleSignOut(false); logout(); }}>Sign out now</Btn>
          <Btn variant="primary" data-testid="idle-stay" onClick={() => markActive(true)}>Stay signed in</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
